import React from 'react';
import { Text } from '@fluentui/react-components';
import { Box, useFluentTokens } from '../../../../theme/fluent';
import type { ApiFollowUpTag } from '../../types/api';

interface FollowUpTagsProps {
  tags: ApiFollowUpTag[];
  onTagClick: (prompt: string) => void;
  isDisabled?: boolean;
}

export const FollowUpTags: React.FC<FollowUpTagsProps> = ({
  tags,
  onTagClick,
  isDisabled = false,
}) => {
  const { tokens, modeTokens } = useFluentTokens();

  if (!tags || tags.length === 0) {
    return null;
  }

  return (
    <Box
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalS,
        width: '100%',
        borderTop: `1px solid ${modeTokens.colorNeutralStroke2}`,
        paddingTop: tokens.spacingVerticalM,
      }}
    >
      {tags.map((tag) => (
        <Box
          key={tag.id}
          role="button"
          tabIndex={isDisabled ? -1 : 0}
          aria-label={tag.label}
          aria-disabled={isDisabled}
          onClick={() => {
            if (!isDisabled) {
              onTagClick(tag.prompt);
            }
          }}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ' ') && !isDisabled) {
              e.preventDefault();
              onTagClick(tag.prompt);
            }
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: tokens.spacingHorizontalS,
            border: `1px solid ${modeTokens.colorNeutralStroke1}`,
            borderRadius: tokens.borderRadiusXXLarge,
            padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalM}`,
            cursor: isDisabled ? 'not-allowed' : 'pointer',
            backgroundColor: modeTokens.colorNeutralBackground1,
            opacity: isDisabled ? 0.5 : 1,
            transition: 'all 0.15s ease',
            minHeight: '32px',
            outline: 'none',
          }}
          onMouseEnter={(e) => {
            if (!isDisabled) {
              e.currentTarget.style.backgroundColor = modeTokens.colorNeutralBackground2;
              e.currentTarget.style.borderColor = modeTokens.colorNeutralStroke2;
            }
          }}
          onMouseLeave={(e) => {
            if (!isDisabled) {
              e.currentTarget.style.backgroundColor = modeTokens.colorNeutralBackground1;
              e.currentTarget.style.borderColor = modeTokens.colorNeutralStroke1;
            }
          }}
          onFocus={(e) => {
            if (!isDisabled) {
              e.currentTarget.style.borderColor = modeTokens.colorBrandStroke;
            }
          }}
          onBlur={(e) => {
            if (!isDisabled) {
              e.currentTarget.style.borderColor = modeTokens.colorNeutralStroke1;
            }
          }}
        >
          <Text
            size={300}
            style={{
              color: isDisabled
                ? modeTokens.colorNeutralForeground4
                : modeTokens.colorNeutralForeground1,
            }}
          >
            {tag.label}
          </Text>
        </Box>
      ))}
    </Box>
  );
};
